import { useQuery } from "@tanstack/react-query";
import { customerAPI, dashboardAPI } from "../../utils/api";
import { Link } from "react-router-dom"; // <-- ADD THIS IMPORT
import {
  Package,
  Clock,
  CheckCircle,
  MapPin,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Truck,
  AlertCircle,
  Loader2,
  Calendar,
  ShoppingBag,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useMemo } from "react";
import { TelegramBanner } from "../../components/TelegramBanner";

const formatMoney = (val) => {
  const num =
    typeof val === "string"
      ? parseFloat(val)
      : typeof val === "number"
        ? val
        : 0;
  return isNaN(num)
    ? "0.00"
    : num.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
};

const formatNumber = (val) => {
  const num =
    typeof val === "string"
      ? parseInt(val, 10)
      : typeof val === "number"
        ? val
        : 0;
  return isNaN(num) ? "0" : num.toLocaleString("en-US");
};

const StatCard = ({
  title,
  value,
  icon: Icon,
  color = "blue",
  trend,
  trendValue,
  isLoading,
}) => {
  const colors = {
    blue: "bg-blue-50 text-blue-600 border-blue-100",
    green: "bg-emerald-50 text-emerald-600 border-emerald-100",
    yellow: "bg-amber-50 text-amber-600 border-amber-100",
    purple: "bg-violet-50 text-violet-600 border-violet-100",
    cyan: "bg-cyan-50 text-cyan-600 border-cyan-100",
  };
  return (
    <div
      className={`card border ${colors[color]} hover:shadow-md transition-shadow duration-200`}
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-500 truncate">{title}</p>
          {isLoading ? (
            <div className="mt-2 h-8 flex items-center">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : (
            <p className="text-2xl font-bold text-gray-900 mt-2 truncate">
              {value}
            </p>
          )}
          {trend && !isLoading && (
            <div
              className={`flex items-center mt-2 text-sm font-medium ${trend === "up" ? "text-emerald-600" : "text-rose-600"}`}
            >
              {trend === "up" ? (
                <TrendingUp className="w-4 h-4 mr-1 flex-shrink-0" />
              ) : (
                <TrendingDown className="w-4 h-4 mr-1 flex-shrink-0" />
              )}
              <span className="truncate">{trendValue}</span>
            </div>
          )}
        </div>
        <div
          className={`p-3 rounded-xl flex-shrink-0 ${colors[color].replace("border-", "bg-").replace("50", "100")}`}
        >
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
};

const SkeletonCard = () => (
  <div className="card animate-pulse">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="h-4 bg-gray-200 rounded w-24"></div>
        <div className="h-8 bg-gray-200 rounded w-16 mt-3"></div>
        <div className="h-4 bg-gray-200 rounded w-20 mt-2"></div>
      </div>
      <div className="h-12 w-12 bg-gray-200 rounded-xl"></div>
    </div>
  </div>
);

const EmptyChart = ({ title }) => (
  <div className="card flex flex-col items-center justify-center h-[250px]">
    <Calendar className="w-12 h-12 text-gray-300 mb-3" />
    <p className="text-gray-500 font-medium">{title}</p>
    <p className="text-sm text-gray-400 mt-1">No data available</p>
  </div>
);

const statusConfig = {
  pending: {
    color: "bg-amber-100 text-amber-800 border-amber-200",
    icon: Clock,
    label: "Pending",
  },
  assigned: {
    color: "bg-blue-100 text-blue-800 border-blue-200",
    icon: Truck,
    label: "Assigned",
  },
  picked_up: {
    color: "bg-violet-100 text-violet-800 border-violet-200",
    icon: Package,
    label: "Picked Up",
  },
  in_transit: {
    color: "bg-cyan-100 text-cyan-800 border-cyan-200",
    icon: MapPin,
    label: "In Transit",
  },
  delivered: {
    color: "bg-emerald-100 text-emerald-800 border-emerald-200",
    icon: CheckCircle,
    label: "Delivered",
  },
  failed: {
    color: "bg-rose-100 text-rose-800 border-rose-200",
    icon: AlertCircle,
    label: "Failed",
  },
  cancelled: {
    color: "bg-gray-100 text-gray-800 border-gray-200",
    icon: AlertCircle,
    label: "Cancelled",
  },
};

const CustomerDashboard = () => {
  const {
    data: statsData,
    isLoading: statsLoading,
    error: statsError,
  } = useQuery({
    queryKey: ["customerStats"],
    queryFn: () => customerAPI.dashboard(),
    refetchInterval: 30000,
    retry: 2,
  });

  const { data: ordersData, isLoading: ordersLoading } = useQuery({
    queryKey: ["customerOrders"],
    queryFn: () => customerAPI.myOrders({ limit: 5 }),
    retry: 2,
  });

  const { data: chartData, isLoading: chartLoading } = useQuery({
    queryKey: ["customerChart"],
    queryFn: () => dashboardAPI.chartData(30),
    retry: 2,
  });

  const stats = useMemo(() => statsData?.data?.data || {}, [statsData]);
  const orders = useMemo(
    () => ordersData?.data?.data?.data || [],
    [ordersData],
  );
  const chart = useMemo(() => chartData?.data?.data || {}, [chartData]);

  if (statsError) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-rose-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900">
            Failed to load dashboard
          </h3>
          <p className="text-gray-500 mt-2">Please try refreshing the page</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Dashboard</h1>
            <p className="text-gray-500 mt-1">Track your orders and spending</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statsLoading ? (
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : (
            <>
              <StatCard
                title="Total Orders"
                value={formatNumber(stats.total_orders)}
                icon={ShoppingBag}
                color="blue"
                trend="up"
                trendValue={`+${formatNumber(stats.this_month_orders || 0)} this month`}
              />
              <StatCard
                title="Total Spent"
                value={`EGP ${formatMoney(stats.total_spent)}`}
                icon={DollarSign}
                color="green"
                trend="up"
                trendValue={`EGP ${formatMoney(stats.this_month_spent || 0)} this month`}
              />
              <StatCard
                title="Active Orders"
                value={formatNumber(stats.active_orders)}
                icon={Package}
                color="yellow"
              />
              <StatCard
                title="Delivered"
                value={formatNumber(stats.delivered_orders)}
                icon={CheckCircle}
                color="purple"
              />
            </>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="card lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Package className="w-5 h-5 mr-2 text-blue-500" />
                Recent Orders
              </h3>
              <Link
                to="/customer/orders"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                View All
              </Link>
            </div>
            {ordersLoading ? (
              <div className="h-[300px] flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : orders.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <ShoppingBag className="w-16 h-16 mb-4 text-gray-300" />
                <p className="font-medium text-lg">No orders yet</p>
                <p className="text-sm mt-1">
                  Create your first order to get started
                </p>
                <Link
                  to="/customer/new-order"
                  className="mt-4 btn-primary text-sm"
                >
                  Create Order
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {orders.map((order) => {
                  const config = statusConfig[order.status];
                  const StatusIcon = config?.icon || Clock;
                  return (
                    <div
                      key={order.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <div
                          className={`p-2 rounded-lg ${config?.color || "bg-gray-100"}`}
                        >
                          <StatusIcon className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="font-mono text-sm font-semibold text-gray-900">
                            {order.tracking_number}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {order.delivery_address?.substring(0, 40)}...
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">
                          EGP {formatMoney(order.total_amount)}
                        </p>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${config?.color || "bg-gray-100"}`}
                        >
                          {config?.label || order.status}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <DollarSign className="w-5 h-5 mr-2 text-emerald-500" />
              Spending
            </h3>
            {chartLoading ? (
              <div className="h-[250px] flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : chart.revenue_per_day && chart.revenue_per_day.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={chart.revenue_per_day}>
                  <defs>
                    <linearGradient
                      id="spendGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: "#6b7280" }}
                    tickLine={false}
                    axisLine={{ stroke: "#e5e7eb" }}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#6b7280" }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => `EGP ${v}`}
                  />
                  <Tooltip
                    formatter={(value) => [`EGP ${value}`, "Spent"]}
                    contentStyle={{
                      borderRadius: "8px",
                      border: "1px solid #e5e7eb",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#10b981"
                    strokeWidth={2}
                    fill="url(#spendGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart title="No spending data yet" />
            )}
          </div>
        </div>
      </div>

      <TelegramBanner />
    </>
  );
};

export default CustomerDashboard;
